import { Link, createFileRoute, useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { Button } from '../components/ui/Button'
import { Card } from '../components/ui/Card'
import { Input } from '../components/ui/Input'
import { useAuth } from '../lib/auth'
import { supabase } from '../lib/supabase'

/**
 * Magic-link sign-in.
 *
 * Search params (all optional):
 *   ?email=  — pre-fill (used by /accept-invite to lock the recipient address)
 *   ?invite= — invitation token; threaded through to /auth/callback so the
 *              recipient lands back on /accept-invite/<token> after sign-in
 *   ?next=   — non-invite redirect target (defaults to /)
 *
 * The "check your email" state is intentionally re-entrant: the user can
 * tap "try a different email" without losing the magic-link session that's
 * still in flight.
 */
const loginSearchSchema = z.object({
  email: z.string().email().optional(),
  invite: z.string().optional(),
  next: z.string().optional(),
})

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: loginSearchSchema,
})

function LoginPage() {
  const search = useSearch({ from: '/login' })
  const { signInWithEmail, signInWithProvider } = useAuth()
  const [email, setEmail] = useState(search.email ?? '')
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [googleSubmitting, setGoogleSubmitting] = useState(false)
  // Forgot-password flow (F4 mirror of apps/session): idle → form → sent.
  const [forgotMode, setForgotMode] = useState(false)
  const [forgotSent, setForgotSent] = useState(false)
  const lockedEmail = Boolean(search.invite && search.email)

  async function handleGoogle() {
    setError(null)
    setGoogleSubmitting(true)
    try {
      await signInWithProvider('google', {
        inviteToken: search.invite,
        redirectTo: search.next,
      })
      // Browser navigates to Google; if we return here, signInWithOAuth threw.
    } catch (err) {
      setError(
        err instanceof Error
          ? `Google sign-in unavailable: ${err.message}. Try email instead.`
          : 'Google sign-in failed',
      )
      setGoogleSubmitting(false)
    }
  }

  if (forgotSent) {
    return (
      <Card className="w-full max-w-[420px] mx-auto my-8 sm:my-16" style={{ maxWidth: 420 }}>
        <h1 style={{ marginTop: 0 }}>Check your email</h1>
        <p>
          If <strong>{email}</strong> has an account, a password reset email is on its way. The
          link brings you back here to set a new password.
        </p>
        <p style={{ color: '#6b7280', fontSize: 13 }}>
          Nothing after a few minutes? Check spam, or request another link.
        </p>
        <Button
          variant="ghost"
          className="w-full sm:w-auto"
          style={{ marginTop: 12 }}
          onClick={() => {
            setForgotSent(false)
            setForgotMode(false)
            setError(null)
          }}
        >
          Back to sign in
        </Button>
      </Card>
    )
  }

  if (forgotMode) {
    return (
      <Card className="w-full max-w-[420px] mx-auto my-8 sm:my-16" style={{ maxWidth: 420 }}>
        <h1 style={{ marginTop: 0 }}>Reset your password</h1>
        <p style={{ color: '#6b7280' }}>
          Enter your account email and we&apos;ll send a link to set a new password.
        </p>
        <form
          onSubmit={async (e) => {
            e.preventDefault()
            setError(null)
            setSubmitting(true)
            try {
              const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/reset-password`,
              })
              if (err) throw err
              setForgotSent(true)
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Could not send the reset email')
            } finally {
              setSubmitting(false)
            }
          }}
        >
          <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Email</label>
          <Input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            disabled={submitting}
            className="w-full"
          />
          {error ? <p style={{ color: '#b91c1c', fontSize: 13, marginTop: 8 }}>{error}</p> : null}
          <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Button
              type="submit"
              disabled={submitting || !email}
              data-testid="auth-forgot-submit"
            >
              {submitting ? 'Sending...' : 'Send reset link'}
            </Button>
            <Button
              variant="ghost"
              type="button"
              onClick={() => {
                setForgotMode(false)
                setError(null)
              }}
            >
              Back to sign in
            </Button>
          </div>
        </form>
      </Card>
    )
  }

  if (sent) {
    return (
      <Card className="w-full max-w-[420px] mx-auto my-8 sm:my-16" style={{ maxWidth: 420 }}>
        <h1 style={{ marginTop: 0 }}>Check your email</h1>
        <p>
          We sent a sign-in link to <strong>{email}</strong>. Open it on this device to continue.
        </p>
        <p style={{ color: '#6b7280', fontSize: 13 }}>The link expires in 60 minutes.</p>
        <Button
          variant="ghost"
          className="w-full sm:w-auto"
          style={{ marginTop: 12 }}
          onClick={() => {
            setSent(false)
            setError(null)
          }}
        >
          Try a different email
        </Button>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-[420px] mx-auto my-8 sm:my-16" style={{ maxWidth: 420 }}>
      <h1 style={{ marginTop: 0 }}>Sign in to OHEAR.AI</h1>
      <p style={{ color: '#6b7280' }}>
        {search.invite
          ? "Enter the email this invitation was sent to. We'll send you a sign-in link."
          : 'Pick the fastest way in.'}
      </p>

      {!search.invite ? (
        <>
          <Button
            variant="ghost"
            type="button"
            onClick={handleGoogle}
            disabled={googleSubmitting || submitting}
            className="w-full"
            style={{
              marginBottom: 14,
              border: '1px solid #d2d2d7',
              background: 'white',
              color: '#1d1d1f',
              fontWeight: 500,
              padding: '10px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path
                d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
                fill="#4285F4"
              />
              <path
                d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
                fill="#34A853"
              />
              <path
                d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
                fill="#FBBC05"
              />
              <path
                d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
                fill="#EA4335"
              />
            </svg>
            {googleSubmitting ? 'Redirecting...' : 'Continue with Google'}
          </Button>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              margin: '16px 0',
              fontSize: 12,
              color: '#86868b',
            }}
          >
            <span style={{ flex: 1, height: 1, background: '#d2d2d7' }} />
            OR
            <span style={{ flex: 1, height: 1, background: '#d2d2d7' }} />
          </div>
        </>
      ) : null}

      <form
        onSubmit={async (e) => {
          e.preventDefault()
          setError(null)
          setSubmitting(true)
          try {
            await signInWithEmail(email, {
              inviteToken: search.invite,
              redirectTo: search.next,
            })
            setSent(true)
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Sign-in failed')
          } finally {
            setSubmitting(false)
          }
        }}
      >
        <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Email</label>
        <Input
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          disabled={submitting || lockedEmail}
          aria-readonly={lockedEmail}
          className="w-full"
        />
        {lockedEmail ? (
          <p style={{ color: '#6b7280', fontSize: 12, marginTop: 6 }}>
            This invitation is bound to the email above and cannot be changed.
          </p>
        ) : null}
        {error ? <p style={{ color: '#b91c1c', fontSize: 13, marginTop: 8 }}>{error}</p> : null}
        <div style={{ marginTop: 16 }}>
          <Button type="submit" disabled={submitting || !email} className="w-full sm:w-auto">
            {submitting ? 'Sending...' : 'Send magic link'}
          </Button>
        </div>
      </form>
      {!search.invite ? (
        <p style={{ color: '#6b7280', fontSize: 13, marginTop: 18 }}>
          New here?{' '}
          <Link to="/signup" style={{ color: '#1f2937' }}>
            Create an actor account
          </Link>
          {' · '}
          <button
            type="button"
            data-testid="auth-forgot-link"
            onClick={() => {
              setForgotMode(true)
              setError(null)
            }}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: '#1f2937',
              textDecoration: 'underline',
              cursor: 'pointer',
              font: 'inherit',
              fontSize: 13,
            }}
          >
            Forgot password?
          </button>
        </p>
      ) : null}
      <p style={{ color: '#6b7280', fontSize: 12, marginTop: 24 }}>
        By signing in, you agree to our{' '}
        <a href="https://ohear.ai/legal/terms" style={{ color: '#1f2937' }}>
          Terms
        </a>{' '}
        and{' '}
        <a href="https://ohear.ai/legal/privacy" style={{ color: '#1f2937' }}>
          Privacy Policy
        </a>
        .
      </p>
    </Card>
  )
}
